import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Camera, Instagram, Music, Plane, Globe } from 'lucide-react';
import { trackWhatsAppClick } from '../utils/analytics';
import SocialStack from './SocialStack';

/**
 * Solutions / Capabilities section.
 * Replaces the old "portfolio variations" with the 4 commercial pillars
 * that actually convert: Filmagem, Sites, Redes Sociais, Edição.
 * Each card opens WhatsApp with a service-specific pre-filled message
 * and tracks the conversion in GA4.
 */
const PortfolioVideo = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const videoRefs = useRef({});

  const solutions = [
    {
      id: 1,
      icon: Camera,
      title: 'Filmagem por Diária',
      subtitle: 'Equipe + equipamento profissional ao seu serviço',
      description:
        'Contrate nossa equipe por dia para qualquer projeto: institucional, comercial, ensaios, BTS, captação para clientes ou produtoras. Você decide a pauta — entregamos com câmeras Sony 4K, iluminação, áudio profissional e operador experiente.',
      keywords: 'Diária • Equipe Completa • Foz do Iguaçu',
      badge: 'Mais Procurado',
      gradient: 'from-[#4ECDC4]/30 via-[#0A0A0A]/40 to-[#0A0A0A]/95',
      poster: '/images/filmagem-mecanica.jpg',
      video: '/videos/portfolio-filmagem.mp4',
      whatsappMessage:
        'Olá! Quero contratar uma diária de filmagem. Data prevista e tipo de projeto:',
      gaSource: 'solution-filmagem-diaria',
      learnMoreUrl: '/filmagem-foz-do-iguacu',
    },
    {
      id: 2,
      icon: Instagram,
      title: 'Gestão de Redes Sociais',
      subtitle: 'Instagram · Facebook · TikTok · Google',
      description:
        'Gestão completa das suas redes sociais: planejamento estratégico, produção de Reels e TikToks, copywriting, design gráfico, agendamento, gerenciamento de Facebook, anúncios no Google e Meta. Equipe presencial uma vez por semana no seu negócio.',
      keywords: 'Instagram • TikTok • Facebook • Google',
      badge: 'Recorrência',
      gradient: 'from-pink-500/30 via-[#0A0A0A]/50 to-[#0A0A0A]/95',
      customVisual: 'social-stack',
      video: '/videos/portfolio-redes.mp4',
      whatsappMessage:
        'Olá! Quero conhecer o serviço de gestão de redes sociais (Instagram, TikTok, Facebook, Google).',
      gaSource: 'solution-redes-sociais',
    },
    {
      id: 3,
      icon: Plane,
      title: 'Filmagens Aéreas & FPV',
      subtitle: 'Drones cinematográficos indoor e outdoor',
      description:
        'Captação aérea com drones profissionais, filmagem indoor com DJI Avata FPV, tours imobiliários cinematográficos para condomínios, casas, lojas e empreendimentos. Imagens impossíveis com qualquer câmera convencional.',
      keywords: 'Drone FPV • Aérea • Tour Imobiliário',
      badge: 'Diferencial Coutin',
      gradient: 'from-sky-500/30 via-[#0A0A0A]/45 to-[#0A0A0A]/95',
      poster:
        'https://images.unsplash.com/photo-1473968512647-3e447244af8f?q=85&w=1200&auto=format&fit=crop',
      video: '/videos/portfolio-aerea.mp4',
      whatsappMessage:
        'Olá! Quero solicitar uma filmagem com drone (aérea, indoor FPV, tour imobiliário). Detalhes do local:',
      gaSource: 'solution-aerea-fpv',
    },
    {
      id: 4,
      icon: Music,
      title: 'Cobertura de Eventos & Shows',
      subtitle: 'Multi-câmera com captação de áudio profissional',
      description:
        'Cobertura completa de shows, festivais, eventos corporativos, congressos, casamentos, formaturas e ativações de marca. Captação multi-câmera, áudio profissional e edição cinematográfica com cortes para redes sociais.',
      keywords: 'Shows • Eventos • Congressos • Casamentos',
      badge: 'Especialidade',
      gradient: 'from-[#4ECDC4]/30 via-[#0A0A0A]/45 to-[#0A0A0A]/95',
      poster:
        'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?q=85&w=1200&auto=format&fit=crop',
      video: '/videos/portfolio-eventos.mp4',
      whatsappMessage:
        'Olá! Quero contratar cobertura de evento/show. Data, local e tipo de evento:',
      gaSource: 'solution-eventos-shows',
    },
  ];

  useEffect(() => {
    solutions.forEach((item) => {
      if (videoRefs.current[item.id]) {
        videoRefs.current[item.id].playbackRate = 0.8;
      }
    });
  }, []);

  const handleCardHover = (itemId, isHovering) => {
    setHoveredCard(isHovering ? itemId : null);
    const v = videoRefs.current[itemId];
    if (!v) return;
    if (isHovering) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  };

  const handleCardClick = (item) => {
    trackWhatsAppClick(item.gaSource, item.title);
    const msg = encodeURIComponent(item.whatsappMessage);
    window.open(`https://wa.me/5545988068508?text=${msg}`, '_blank');
  };

  return (
    <section
      id="portfolio"
      className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden"
      data-testid="solutions-section"
    >
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle, #4ECDC4 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <div className="inline-flex items-center space-x-2 mb-4">
            <div className="w-8 h-[1px] bg-[#4ECDC4]" />
            <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#4ECDC4]">
              Agência Completa · Foz do Iguaçu
            </span>
            <div className="w-8 h-[1px] bg-[#4ECDC4]" />
          </div>
          <h2 className="text-3xl md:text-5xl tracking-tight font-bold text-[#F5F5F0] mb-4">
            Tudo que sua marca precisa,{' '}
            <span className="text-[#4ECDC4]">em um só lugar</span>
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-[#A1A1AA] max-w-3xl mx-auto font-light">
            Filmagem, sites, redes sociais e edição. Uma equipe local, presente,
            que entende o seu negócio.
          </p>
        </motion.div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-7xl mx-auto">
          {solutions.map((item, index) => {
            const Icon = item.icon;
            const isHover = hoveredCard === item.id;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onMouseEnter={() => handleCardHover(item.id, true)}
                onMouseLeave={() => handleCardHover(item.id, false)}
                onClick={() => handleCardClick(item)}
                className="group relative overflow-hidden rounded-2xl cursor-pointer h-[460px] md:h-[500px] border border-white/10 hover:border-[#4ECDC4]/50 transition-all duration-500"
                data-testid={`solution-card-${item.id}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick(item);
                  }
                }}
              >
                {/* Background: video on hover, poster or custom visual always visible */}
                <div className="absolute inset-0">
                  {item.customVisual === 'social-stack' ? (
                    <SocialStack />
                  ) : (
                    <img
                      src={item.poster}
                      alt={item.title}
                      className={`w-full h-full object-cover transition-all duration-700 ${
                        isHover ? 'scale-110 opacity-90' : 'scale-100 opacity-80'
                      }`}
                      loading="lazy"
                    />
                  )}
                  {item.video && (
                    <video
                      ref={(el) => (videoRefs.current[item.id] = el)}
                      loop
                      muted
                      playsInline
                      poster={item.poster}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                        isHover ? 'opacity-90' : 'opacity-0'
                      }`}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    >
                      <source src={item.video} type="video/mp4" />
                    </video>
                  )}

                  {/* Gradient overlay */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-t ${item.gradient}`}
                  />
                </div>

                {/* Content */}
                <div className="absolute inset-0 p-7 md:p-8 flex flex-col justify-between">
                  {/* Top: badge + icon */}
                  <div className="flex justify-between items-start">
                    <span className="inline-flex items-center bg-[#4ECDC4] text-[#0A0A0A] text-[10px] md:text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-lg">
                      {item.badge}
                    </span>
                    <motion.div
                      animate={{
                        scale: isHover ? 1.1 : 1,
                        rotate: isHover ? 8 : 0,
                      }}
                      transition={{ duration: 0.4 }}
                      className={`w-12 h-12 rounded-full bg-[#4ECDC4]/20 backdrop-blur-md border border-[#4ECDC4]/40 flex items-center justify-center ${
                        item.learnMoreUrl ? 'mt-12' : ''
                      }`}
                    >
                      <Icon className="w-5 h-5 text-[#4ECDC4]" strokeWidth={2} />
                    </motion.div>
                  </div>

                  {/* Bottom: copy */}
                  <div>
                    <p className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#4ECDC4] mb-2 font-bold">
                      {item.keywords}
                    </p>
                    <h3 className="text-2xl md:text-3xl font-black text-[#F5F5F0] mb-2 leading-tight drop-shadow-lg">
                      {item.title}
                    </h3>
                    <p className="text-sm md:text-base font-medium text-[#F5F5F0]/85 mb-4 drop-shadow-md">
                      {item.subtitle}
                    </p>
                    <motion.p
                      initial={false}
                      animate={{
                        opacity: isHover ? 1 : 0.8,
                        height: isHover ? 'auto' : 0,
                      }}
                      transition={{ duration: 0.4 }}
                      className="text-sm text-[#F5F5F0]/75 font-light leading-relaxed overflow-hidden drop-shadow-md"
                    >
                      <span className="block pt-1 pb-3">{item.description}</span>
                    </motion.p>

                    {/* CTA hint */}
                    <div className="inline-flex items-center gap-2 text-[#4ECDC4] font-bold text-sm uppercase tracking-wider">
                      <span>Solicitar Orçamento</span>
                      <motion.div
                        animate={{ x: isHover ? 4 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ArrowRight className="w-4 h-4" />
                      </motion.div>
                    </div>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: isHover ? '60px' : '0px' }}
                      transition={{ duration: 0.4 }}
                      className="mt-2 h-[2px] bg-[#4ECDC4]"
                    />

                    {/* Learn more link (SEO internal linking) */}
                    {item.learnMoreUrl && (
                      <Link
                        to={item.learnMoreUrl}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-5 right-5 bg-black/55 backdrop-blur-md px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] text-[#F5F5F0] hover:text-[#4ECDC4] transition-colors font-bold border border-[#4ECDC4]/40 z-20"
                        data-testid={`solution-learn-more-${item.id}`}
                      >
                        Saiba mais →
                      </Link>
                    )}
                  </div>
                </div>

                {/* Vignette */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'radial-gradient(circle at center, transparent 40%, rgba(10,10,10,0.5) 100%)',
                  }}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Sites + Landing Pages - Featured Banner (national reach) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-16 max-w-7xl mx-auto"
        >
          <div className="relative overflow-hidden rounded-2xl border border-white/10 hover:border-[#4ECDC4]/50 transition-all duration-500 bg-gradient-to-br from-[#0F1A2E] via-[#0A0A0A] to-[#0A0A0A]">
            {/* Ambient layers */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_50%,rgba(59,130,246,0.16)_0%,rgba(0,0,0,0)_55%)] pointer-events-none" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_70%,rgba(78,205,196,0.14)_0%,rgba(0,0,0,0)_50%)] pointer-events-none" />
            {/* Subtle grid pattern overlay */}
            <div
              className="absolute inset-0 opacity-[0.04] pointer-events-none"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(78,205,196,1) 1px, transparent 1px), linear-gradient(90deg, rgba(78,205,196,1) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }}
            />

            <div className="relative p-8 md:p-14 lg:p-16 text-center">
              <div className="inline-flex items-center bg-[#4ECDC4] text-[#0A0A0A] text-[10px] md:text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-lg mb-5">
                Atendemos Todo o Brasil
              </div>
              <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F5F5F0] leading-tight mb-5 max-w-3xl mx-auto">
                Sites & <span className="text-[#4ECDC4] italic font-light">Landing Pages</span> de Alta Conversão
              </h3>
              <p className="text-base md:text-lg text-[#A1A1AA] mb-7 font-light leading-relaxed max-w-2xl mx-auto">
                Sites institucionais, lojas virtuais, portais corporativos e landing pages otimizadas para
                campanhas de Google Ads e Meta Ads. Design exclusivo, otimização SEO e integrações nativas.
              </p>

              <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mb-8 text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#4ECDC4] font-bold">
                <span>Mobile-First</span>
                <span className="text-white/30">·</span>
                <span>SEO Google</span>
                <span className="text-white/30">·</span>
                <span>WhatsApp Integrado</span>
                <span className="text-white/30">·</span>
                <span>Hospedagem Inclusa</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    trackWhatsAppClick('sites-banner', 'Sites e Landing Pages');
                    window.open(
                      `https://wa.me/5545988068508?text=${encodeURIComponent(
                        'Olá! Quero desenvolver um site ou landing page com a Coutin. Tipo de projeto:'
                      )}`,
                      '_blank'
                    );
                  }}
                  className="group inline-flex items-center justify-center gap-2 bg-[#4ECDC4] hover:bg-[#F5F5F0] text-[#0A0A0A] font-bold uppercase tracking-wide px-7 py-3.5 rounded-lg transition-all duration-300 shadow-xl shadow-[#4ECDC4]/20"
                  data-testid="sites-banner-cta"
                >
                  <span>Solicitar Orçamento</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                <Link
                  to="/criacao-de-sites"
                  className="inline-flex items-center justify-center gap-2 border border-[#4ECDC4]/40 hover:bg-[#4ECDC4]/10 text-[#F5F5F0] font-bold uppercase tracking-wide px-7 py-3.5 rounded-lg transition-all duration-300"
                  data-testid="sites-banner-learn-more"
                >
                  <Globe className="w-4 h-4" />
                  <span>Conhecer Serviço</span>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <p className="text-[#A1A1AA] mb-4 text-base md:text-lg font-light">
            Precisa de mais de um serviço? Combinamos os pacotes para você.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              trackWhatsAppClick(
                'solutions-bottom',
                'Conversa Personalizada'
              );
              const message = encodeURIComponent(
                'Olá! Quero uma conversa estratégica sobre os serviços da Coutin.'
              );
              window.open(
                `https://wa.me/5545988068508?text=${message}`,
                '_blank'
              );
            }}
            className="inline-flex items-center space-x-2 bg-[#4ECDC4] hover:bg-[#F5F5F0] text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 rounded-lg transition-all duration-300 shadow-xl shadow-[#4ECDC4]/20"
            data-testid="solutions-bottom-cta"
          >
            <span>Conversar com a Coutin</span>
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default PortfolioVideo;
